import Ajv2020 from "ajv/dist/2020";
import { ValidateFunction } from "ajv";
import dayjs from "dayjs";
import {
  AutoRecipeParameterDefinition,
  AutoRecipeParameters,
} from "store/api/generatedApi";

type PropertyName = string;
export interface ParameterSchema {
  type?: string;
  format?: string;
  const?: unknown;
  enum?: (string | number)[];
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  anyOf?: ParameterSchema[];
  properties?: Record<PropertyName, ParameterSchema>;
  additionalProperties?: ParameterSchema | boolean;
  items?: ParameterSchema;
  $ref?: string;
  $defs?: Record<PropertyName, ParameterSchema>;
}

export const resolveParameterSchema = (
  schema: ParameterSchema,
  root: ParameterSchema
): ParameterSchema => {
  if (schema.$ref?.startsWith("#/$defs/")) {
    const definition = root.$defs?.[schema.$ref.slice("#/$defs/".length)];
    if (definition) return resolveParameterSchema(definition, root);
  }
  return schema.anyOf
    ? {
        ...schema,
        anyOf: schema.anyOf.map((alternative) =>
          resolveParameterSchema(alternative, root)
        ),
      }
    : schema;
};

export const scalarSchema = (schema: ParameterSchema) =>
  schema.anyOf?.find((alternative) => alternative.type !== "null") ?? schema;

export const nullableSchema = (schema: ParameterSchema) =>
  schema.anyOf?.some((alternative) => alternative.type === "null") ?? false;

export const parameterFieldSchema = ({
  definition,
  section,
  field,
}: {
  definition: AutoRecipeParameterDefinition;
  section: string;
  field: string;
}): ParameterSchema => {
  const root: ParameterSchema = definition.json_schema;
  const sectionSchema = resolveParameterSchema(
    root.properties?.[section] ?? {},
    root
  );
  return resolveParameterSchema(sectionSchema.properties?.[field] ?? {}, root);
};

export const createParameterValidator = (
  definition: AutoRecipeParameterDefinition
) => {
  const validator = new Ajv2020({ allErrors: true });
  validator.addFormat("date", {
    type: "string",
    validate: (date: string) =>
      /^\d{4}-\d{2}-\d{2}$/.test(date) &&
      dayjs(date).isValid() &&
      dayjs(date).format("YYYY-MM-DD") === date,
  });
  validator.addFormat("date-time", {
    type: "string",
    validate: (timestamp: string) =>
      /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(timestamp) &&
      dayjs(timestamp).isValid(),
  });

  let validate: ValidateFunction;
  try {
    validate = validator.compile(definition.json_schema);
  } catch {
    return () => ({
      valid: false,
      supported: false,
      invalidPaths: [] as string[][],
    });
  }
  const supported = supportsParameterDefinition(definition);
  return (parameters: AutoRecipeParameters) => {
    const valid = validate(parameters) && supported;
    return {
      valid,
      supported,
      invalidPaths: (validate.errors ?? []).map((error) => [
        ...error.instancePath
          .split("/")
          .slice(1)
          .map((part) => part.replaceAll("~1", "/").replaceAll("~0", "~")),
        ...(error.keyword === "required"
          ? [String(error.params.missingProperty)]
          : []),
      ]),
    };
  };
};

export const supportsParameterDefinition = (
  definition: AutoRecipeParameterDefinition
) =>
  definition.sections.every((section) =>
    section.fields.every((field) => {
      const schema = parameterFieldSchema({
        definition,
        section: section.name,
        field: field.name,
      });
      switch (field.widget) {
        case "inventory_targets":
        case "conditional_inventory_targets":
        case "material_exclusivity":
        case "material_limit_overrides":
        case "steel_grades":
        case "time_intervals":
          return schema.type === "array";
        case "monthly_targets":
          return schema.type === "object";
        case "month":
          return (
            scalarSchema(schema).type === "string" &&
            scalarSchema(schema).format === "date"
          );
        case "scalar":
          return (
            (!schema.anyOf ||
              schema.anyOf.filter((alternative) => alternative.type !== "null")
                .length === 1) &&
            ["number", "integer", "string"].includes(
              scalarSchema(schema).type ?? ""
            )
          );
        default:
          return false;
      }
    })
  );

export type ParameterValidation = ReturnType<
  ReturnType<typeof createParameterValidator>
>;
